import React, { ChangeEvent, useState } from "react";
import { CloseIcon } from "../../../assets/icons";
import PhoneInputComponent from "../../common/PhoneInputComponent";
import { validatePhoneNumber } from "../../../utils";
import { ICustomer } from "../../../@types";
import { Spinner } from "flowbite-react";
import { useTranslation } from "react-i18next";
import { sendInvoiceReminder } from "../../../backend-services";
import SelectedMarkIcon from "../../../assets/icons/SelectedIcon";
import { showToast } from "../../../utils/toastUtils";

interface SendInvoiceModalProps {
  setShowSendModal: (showInvoice: boolean) => void;
  setIsSending: (isSending: boolean) => void;
  customer: ICustomer;
  invoiceId?: string;
}

const SendInvoiceModal = ({
  setShowSendModal,
  customer,
  invoiceId,
  setIsSending,
}: SendInvoiceModalProps) => {
  const { t } = useTranslation("invoices");
  const [medium, setMedium] = useState({
    sms: true,
    whatsapp: false,
    email: false,
  });
  const [phoneNumber, setPhoneNumber] = useState(customer?.phone || "");
  const [email, setEmail] = useState(customer?.email || "");
  const [validatePhone, setValidatePhone] = useState<string>("");
  const [isLoading, setIsLoading] = useState(false);

  const handleMediumChanges = (option: string) => {
    setMedium({
      sms: option === "sms",
      whatsapp: option === "whatsapp",
      email: option === "email",
    });
  };

  const handlePhoneNumberChange = (value: string) => {
    if (value) {
      const trimmedValue = value.trim();
      const normalizedValue = trimmedValue.replace(/\D/g, "");

      const isValidPhoneNumber = validatePhoneNumber(normalizedValue);

      if (isValidPhoneNumber) {
        setPhoneNumber("+" + normalizedValue);
        setValidatePhone("");
      } else {
        setValidatePhone("Invalid phone number format");
      }
    } else {
      setPhoneNumber("");
      setValidatePhone("");
    }
  };

  const handlePhoneNumberKeyDown = (
    event: React.KeyboardEvent<HTMLInputElement>,
  ) => {
    if (event.currentTarget.value.length >= 17) {
      event.preventDefault();
    }
  };

  const handleSubmit = async () => {
    try {
      setIsLoading(true);
      if (invoiceId) {
        const res = await sendInvoiceReminder(invoiceId, medium);
        if (res) {
          showToast("Invoice sent successfully", "success");
          setIsSending(true);
          setShowSendModal(false);
        }
      }
    } catch (error) {
      showToast("Failed to send invoice. Please try again.", "error");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <div className="bg-gray-900 bg-opacity-50 fixed top-0 right-0 left-0 z-50 h-[100vh]">
        <div className="flex items-end justify-center md:items-center h-full w-full">
          <div
            className={`overflow-y-auto overflow-x-hidden fixed md:top-[25%] w-full md:w-[500px] p-6  bg-white rounded-[16px]`}
          >
            <div className="flex justify-between items-center cursor-pointer mb-2">
              <h1 className="text-[16px] md:text-[24px]  leading-7 font-medium">
                Send Invoice
              </h1>
              <div onClick={() => setShowSendModal(false)}>
                <CloseIcon color="#0A0A0A" />
              </div>
            </div>
            <p className="text-[16px] font-medium md:text-[16px] text-start leading-5 text-[#292D32] mb-7">
              Send invoices to your clients via SMS or email
            </p>

            <div>
              {customer?.phone && (
                <div
                  className={`px-[1em] py-2 rounded-md cursor-pointer mb-3 ${
                    medium.sms ? "bg-gray-100" : "bg-white"
                  }`}
                  onClick={() => handleMediumChanges("sms")}
                >
                  <div className="flex justify-between items-center">
                    <span className="text-[18px] font-medium">
                      Share Via SMS
                    </span>
                    {medium.sms ? (
                      <SelectedMarkIcon />
                    ) : (
                      <span className="w-5 h-5 rounded-full border-2 border-gray-300"></span>
                    )}
                  </div>
                  {medium.sms && (
                    <div className="mt-[0.1em]">
                      <p className="text-[16px] text-start text-gray-600">
                        {customer.phone}
                      </p>
                    </div>
                  )}
                </div>
              )}

              {customer?.email && (
                <div
                  className={`px-[1em] py-2 rounded-md cursor-pointer mt-2 mb-3 ${
                    medium.email ? "bg-gray-100" : "bg-white"
                  }`}
                  onClick={() => handleMediumChanges("email")}
                >
                  <div className="flex justify-between items-center">
                    <span className="text-[18px] font-medium">
                      Share Via Email
                    </span>
                    {medium.email ? (
                      <SelectedMarkIcon />
                    ) : (
                      <span className="w-5 h-5 rounded-full border-2 border-gray-300"></span>
                    )}
                  </div>
                  {medium.email && (
                    <div className="mt-[0.1em]">
                      <p className="text-[16px] text-start text-gray-600">
                        {customer.email}
                      </p>
                    </div>
                  )}
                </div>
              )}

              {/* WhatsApp option - uncomment if needed */}
              {customer?.phone && (
                <div
                  className={`px-[1em] py-2 rounded-md cursor-pointer mb-3 ${
                    medium.whatsapp ? "bg-gray-100" : "bg-white"
                  }`}
                  onClick={() => handleMediumChanges("whatsapp")}
                >
                  <div className="flex justify-between items-center">
                    <span className="text-[18px] font-medium">
                      Share Via WhatsApp
                    </span>
                    {medium.whatsapp ? (
                      <SelectedMarkIcon />
                    ) : (
                      <span className="w-5 h-5 rounded-full border-2 border-gray-300"></span>
                    )}
                  </div>
                  {medium.whatsapp && (
                    <div className="mt-[0.1em]">
                      <p className="text-[16px] text-start text-gray-600">
                        {customer.phone}
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
            <button
              onClick={handleSubmit}
              className="bg-[#044e97] w-full h-[48px] mt-5 font-semibold text-white flex justify-center items-center rounded-[4px]"
            >
              {isLoading ? <Spinner /> : "Send Invoice"}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default SendInvoiceModal;
